import { useCallback } from "react";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: "default" | "destructive";
  duration?: number;
  action?: ToastAction;
}

export function useToast() {
  const { addToast, removeToast } = useEnhancedToast();

  const toast = useCallback(
    (options: ToastOptions) => {
      // Map variant to enhanced toast component variant
      const variant = options.variant === "destructive" ? "error" : "default";

      const toastId = addToast({
        title: options.title,
        description: options.description,
        variant,
        duration: options.duration,
        action: options.action,
      });

      return toastId;
    },
    [addToast],
  );

  return {
    toast,
    dismiss: removeToast,
  };
}

// Export a standalone toast function for convenience
export const toast = (options: ToastOptions) => {
  const toastEl = document.querySelector("[data-toast-container]");
  if (!toastEl) {
    console.error(
      "Toast container not found. Make sure EnhancedToastProvider is mounted.",
    );
    return;
  }

  // Dispatch a custom event that the provider can listen to
  const event = new CustomEvent("app:toast", { detail: options });
  window.dispatchEvent(event);
};
